<template>
  <form
    class="v3-form behaviour-point-correction-form"
    data-testid="behaviour-point-correction-form"
    @submit.prevent="onSubmit"
  >
    <InfoBox
      v-if="state.serverMessage"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      :message="state.serverMessage"
      announce-assertive
    />

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-correction-points-lbl"
        html-for="behaviour-correction-points-input"
      >
        {{ fieldLabels.pointsUsed }}
      </Label>
      <p id="behaviour-correction-points-hint" class="text-danger small mb-2">
        {{ pointsCorrectedHint }}
      </p>
      <InputField
        id="behaviour-correction-points-input"
        label-id="behaviour-correction-points-lbl"
        class="w-100"
        input-type="number"
        :model-value="state.form.points"
        :invalid-feedback="errors.points"
        data-testid="behaviour-correction-points-input"
        @update:model-value="(v) => patchForm('points', v)"
      />
    </div>

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-correction-reason-lbl"
        html-for="behaviour-correction-reason-input"
      >
        {{ $t("behaviour_manage.correction_reason_label") }}
      </Label>
      <InputField
        id="behaviour-correction-reason-input"
        label-id="behaviour-correction-reason-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.reason"
        :invalid-feedback="errors.reason"
        data-testid="behaviour-correction-reason-input"
        @update:model-value="(v) => patchForm('reason', v)"
      />
    </div>

    <BehaviourSelectStudentsFieldset
      v-model="state.form.studentIds"
      class="mb-3"
      :invalid-feedback="errors.studentIds"
      label-id="behaviour-correction-students-lbl"
      input-id="behaviour-correction-students-input"
      data-testid="behaviour-correction-students-select"
    />

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-correction-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        data-testid="behaviour-correction-submit"
      >
        {{ $t("behaviour_manage.correction_submit") }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import BehaviourSelectStudentsFieldset from "@/v3components/Forms/Behaviour/BehaviourSelectStudentsFieldset.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import {
  normalizeBehaviourStudentIds,
  parseBehaviourPointsApiError
} from "@/helpers/behaviourPointsForm.js"
import { computed, inject, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourPointCorrectionForm",
  components: {
    BehaviourSelectStudentsFieldset,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const fieldLabels = computed(() => ({
      pointsUsed: t("behaviour_manage.points_corrected_label", {
        points: behaviourTerminology.value.points
      })
    }))

    const pointsCorrectedHint = computed(() =>
      t("behaviour_manage.points_corrected_hint", {
        points: behaviourTerminology.value.points
      })
    )

    const state = reactive({
      form: {
        points: "",
        reason: "",
        studentIds: []
      },
      apiFieldErrors: {},
      isProcessing: false,
      isServerError: false,
      serverTitle: "",
      serverMessage: "",
      touched: false
    })

    const clientErrors = computed(() => {
      if (!state.touched) return {}
      const out = {}
      if (!String(state.form.points ?? "").trim()) {
        out.points = t("behaviour_manage.error_required_field", {
          field: fieldLabels.value.pointsUsed
        })
      }
      if (!String(state.form.reason ?? "").trim()) {
        out.reason = t("behaviour_manage.error_required_field", {
          field: t("behaviour_manage.correction_reason_label")
        })
      }
      if (!normalizeBehaviourStudentIds(state.form.studentIds).length) {
        out.studentIds = t("behaviour_manage.error_select_students")
      }
      return out
    })

    const errors = computed(() => ({
      ...clientErrors.value,
      ...state.apiFieldErrors
    }))

    const patchForm = (key, value) => {
      state.form[key] = value ?? (key === "studentIds" ? [] : "")
      if (state.apiFieldErrors[key]) {
        const next = { ...state.apiFieldErrors }
        delete next[key]
        state.apiFieldErrors = next
      }
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""
    }

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""

      if (Object.keys(clientErrors.value).length) return

      try {
        state.isProcessing = true
        const result = await store.dispatch(
          "behaviourManage/createPointsCorrection",
          state.form
        )
        modal?.setAction?.("pointHistoryCorrected", {
          ...state.form,
          ...result
        })
        emit("cancel")
      } catch (error) {
        const parsed = parseBehaviourPointsApiError(error, "correction")
        if (Object.keys(parsed.fieldErrors).length) {
          state.apiFieldErrors = parsed.fieldErrors
        }

        state.isServerError = true
        state.serverTitle = t("behaviour_manage.error_title")
        state.serverMessage =
          parsed.message || t("behaviour_manage.correction_error")
      } finally {
        state.isProcessing = false
      }
    }

    return {
      fieldLabels,
      pointsCorrectedHint,
      state,
      errors,
      patchForm,
      onCancel,
      onSubmit
    }
  }
}
</script>
